import React from 'react';
import { cn } from '@/utils/cn';
import { HelpCircle } from 'lucide-react';

export interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  className,
}) => {
  const positionClasses: Record<string, string> = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowClasses: Record<string, string> = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-[#1a1a2e] border-x-transparent border-b-transparent border-[4px]',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-[#1a1a2e] border-x-transparent border-t-transparent border-[4px]',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-[#1a1a2e] border-y-transparent border-r-transparent border-[4px]',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-[#1a1a2e] border-y-transparent border-l-transparent border-[4px]',
  };

  return (
    <span className={cn('relative inline-flex group', className)}>
      {children}
      <span
        className={cn(
          'absolute z-50 px-2.5 py-1.5 text-xs text-gray-900 bg-white border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] rounded-lg whitespace-normal max-w-[240px] leading-relaxed',
          'opacity-0 invisible group-hover:opacity-100 group-hover:visible',
          'transition-all duration-150 pointer-events-none',
          positionClasses[position],
        )}
      >
        {content}
        <span className={cn('absolute w-0 h-0', arrowClasses[position])} />
      </span>
    </span>
  );
};

/** A small (i) icon with a tooltip — use next to field labels */
export const InfoTooltip: React.FC<{
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}> = ({ content, position = 'top' }) => (
  <Tooltip content={content} position={position}>
    <HelpCircle className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 transition-colors cursor-help" />
  </Tooltip>
);

Tooltip.displayName = 'Tooltip';
